import React, { useState } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip, ResponsiveContainer,
  BarChart, CartesianGrid, XAxis, YAxis, Bar
} from 'recharts';
import SetScenariosChart from './SetScenariosChart';
import MatchAnalysisSummary from './MatchAnalysisSummary';
import { calculateSetScenarios, generateMatchAnalysisSummary, getAlgorithmImprovements } from '../utils';

function SurfaceStatsDetail({ surface, comparison, player1, player2, getAiSummary, setAiSummary }) {
  const [isGrandSlam, setIsGrandSlam] = useState(false);
  const [showAlgorithmInfo, setShowAlgorithmInfo] = useState(false);

  // Vérifications encore plus strictes pour éviter les erreurs
  if (!comparison || 
      !comparison.advanced || 
      !comparison.advanced.factors ||
      typeof comparison.advanced.player1 !== 'number' ||
      typeof comparison.advanced.player2 !== 'number') {
    return (
      <div style={{
        marginTop: 20,
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        padding: 15,
        backgroundColor: '#f9f9f9',
        textAlign: 'center'
      }}>
        <h4 style={{marginTop: 0, marginBottom: 15}}>Statistiques détaillées - {surface}</h4>
        <p>Les données détaillées ne sont pas disponibles pour ce match sur cette surface.</p>
      </div>
    );
  }
  
  // Utilisation de l'opérateur de chaînage optionnel pour être sûr que factors existe
  const factors = comparison?.advanced?.factors || {};
  
  // Vérifications renforcées pour chaque propriété utilisée
  const hasServiceGameProb = factors.serviceGameProb && 
                            typeof factors.serviceGameProb.player1 === 'number' && 
                            typeof factors.serviceGameProb.player2 === 'number';
  
  const hasServiceStats = factors.serviceStats && 
                         factors.serviceStats.player1 && 
                         factors.serviceStats.player2 &&
                         typeof factors.serviceStats.player1.firstServeWin === 'number' &&
                         typeof factors.serviceStats.player1.secondServeWin === 'number' &&
                         typeof factors.serviceStats.player2.firstServeWin === 'number' &&
                         typeof factors.serviceStats.player2.secondServeWin === 'number';
  
  const hasRecentForm = factors.recentForm && 
                       typeof factors.recentForm.player1 === 'number' && 
                       typeof factors.recentForm.player2 === 'number';
  
  const hasMatchStatProb = factors.matchStatProb && 
                          typeof factors.matchStatProb.player1 === 'number' && 
                          typeof factors.matchStatProb.player2 === 'number';
                          
  // Vérification des propriétés simpleElo et advanced
  const hasSimpleElo = comparison.simpleElo && 
                      typeof comparison.simpleElo.player1 === 'number' &&
                      typeof comparison.simpleElo.player2 === 'number';
                      
  const hasAdvancedProb = typeof comparison.advanced.player1 === 'number' &&
                         typeof comparison.advanced.player2 === 'number';

  // Génère les données du radar de manière sécurisée
  const radarData = [
    {
      stat: "Service",
      [player1.player]: hasServiceGameProb ? factors.serviceGameProb.player1 * 100 : 0,
      [player2.player]: hasServiceGameProb ? factors.serviceGameProb.player2 * 100 : 0,
    },
    {
      stat: "Retour",
      [player1.player]: hasServiceGameProb ? (1 - factors.serviceGameProb.player2) * 100 : 0,
      [player2.player]: hasServiceGameProb ? (1 - factors.serviceGameProb.player1) * 100 : 0,
    },
    {
      stat: "1er Service",
      [player1.player]: hasServiceStats ? factors.serviceStats.player1.firstServeWin * 100 : 0,
      [player2.player]: hasServiceStats ? factors.serviceStats.player2.firstServeWin * 100 : 0,
    },
    {
      stat: "2e Service",
      [player1.player]: hasServiceStats ? factors.serviceStats.player1.secondServeWin * 100 : 0,
      [player2.player]: hasServiceStats ? factors.serviceStats.player2.secondServeWin * 100 : 0,
    },
    {
      stat: "Forme Récente",
      [player1.player]: hasRecentForm ? factors.recentForm.player1 * 100 : 0,
      [player2.player]: hasRecentForm ? factors.recentForm.player2 * 100 : 0,
    }
  ];

  // Génère les données du graphique à barres de manière sécurisée
  const probabilityData = [
    {
      model: "Elo",
      [player1.player]: comparison.simpleElo && comparison.simpleElo.player1 ? comparison.simpleElo.player1 : 0,
      [player2.player]: comparison.simpleElo && comparison.simpleElo.player2 ? comparison.simpleElo.player2 : 0,
    },
    {
      model: "Service/Retour",
      [player1.player]: hasMatchStatProb ? Math.round(factors.matchStatProb.player1 * 100) : 0,
      [player2.player]: hasMatchStatProb ? Math.round(factors.matchStatProb.player2 * 100) : 0,
    },
    {
      model: "Forme Récente",
      [player1.player]: hasRecentForm ? Math.round(factors.recentForm.player1 * 100) : 0,
      [player2.player]: hasRecentForm ? Math.round(factors.recentForm.player2 * 100) : 0,
    },
    {
      model: "Avancé",
      [player1.player]: comparison.advanced.player1 !== undefined ? comparison.advanced.player1 : 0,
      [player2.player]: comparison.advanced.player2 !== undefined ? comparison.advanced.player2 : 0,
    }
  ];
  
  return (
    <div style={{
      marginTop: 20,
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      padding: 15,
      backgroundColor: '#f9f9f9'
    }}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15}}>
        <h4 style={{marginTop: 0, marginBottom: 0}}>Statistiques détaillées - {surface}</h4>
        <button 
          onClick={() => setShowAlgorithmInfo(!showAlgorithmInfo)}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showAlgorithmInfo ? 'Masquer' : 'Infos Algo v2.2'}
        </button>
      </div>
      
      {/* Section d'information sur l'algorithme */}
      {showAlgorithmInfo && (
        <div style={{
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <h5 style={{fontSize: 16, marginBottom: 10, color: '#007bff'}}>
            🚀 Améliorations de l'algorithme v2.2
          </h5>
          {(() => {
            const improvements = getAlgorithmImprovements();
            return (
              <div>
                <div style={{marginBottom: 15}}>
                  <h6 style={{fontSize: 14, marginBottom: 8}}>Nouvelles fonctionnalités :</h6>
                  {improvements.improvements.map((improvement, index) => (
                    <div key={index} style={{marginBottom: 8}}>
                      <strong style={{color: '#28a745'}}>{improvement.title}:</strong> {improvement.description}
                      <div style={{fontSize: 12, color: '#666', marginLeft: 10}}>
                        → {improvement.impact}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{marginBottom: 15}}>
                  <h6 style={{fontSize: 14, marginBottom: 8}}>Nouvelle pondération :</h6>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', fontSize: 13}}>
                    {Object.entries(improvements.weights).map(([key, value]) => (
                      <div key={key} style={{backgroundColor: 'white', padding: '6px', borderRadius: '4px'}}>
                        <strong>{key}:</strong> {value}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h6 style={{fontSize: 14, marginBottom: 8}}>Qualité des données :</h6>
                  <div style={{fontSize: 13}}>
                    {Object.entries(improvements.dataQuality).map(([level, description]) => (
                      <div key={level} style={{marginBottom: 4}}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          backgroundColor: level === 'high' ? '#d4edda' : level === 'medium' ? '#fff3cd' : '#f8d7da',
                          color: level === 'high' ? '#155724' : level === 'medium' ? '#856404' : '#721c24',
                          fontWeight: 'bold',
                          marginRight: 8
                        }}>
                          {level.toUpperCase()}
                        </span>
                        {description}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
      
      {/* AI-powered match analysis summary */}
      <div style={{marginBottom: 20}}>
        <div style={{display: 'flex', alignItems: 'center', marginBottom: 10}}>
          <h5 style={{fontSize: 15, marginBottom: 0, marginRight: 10}}>Analyse IA du match</h5>
          <span style={{
            fontSize: 11,
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '2px 6px',
            borderRadius: '3px',
            fontWeight: 'bold'
          }}>
            Utilise l'algorithme v2.2 (amplification des écarts)
          </span>
        </div>
        <MatchAnalysisSummary 
          player1={player1}
          player2={player2}
          surface={surface}
          comparisonResult={comparison}
          autoGenerate={false}
          getAiSummary={getAiSummary}
          setAiSummary={setAiSummary}
          key={`${surface}-${player1.player}-${player2.player}`}
        />
      </div>
      
      {/* Graphique Radar - Comparaison des compétences */}
      <div style={{marginBottom: 30}}>
        <h5 style={{fontSize: 15, marginBottom: 10}}>Comparaison des compétences (%)</h5>
        <div style={{height: 350}}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart outerRadius={120} width={800} height={350} data={radarData}>
              <PolarGrid />
              <PolarAngleAxis dataKey="stat" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} />
              <Radar
                name={player1.player}
                dataKey={player1.player}
                stroke="#8884d8"
                fill="#8884d8"
                fillOpacity={0.5}
              />
              <Radar
                name={player2.player}
                dataKey={player2.player}
                stroke="#82ca9d"
                fill="#82ca9d"
                fillOpacity={0.5}
              />
              <Legend />
              <Tooltip formatter={(value) => `${Math.round(value)}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Graphique à barres - Probabilités de victoire par modèle */}
      <div style={{marginBottom: 30}}>
        <h5 style={{fontSize: 15, marginBottom: 10}}>Probabilités par modèle (%)</h5>
        <div style={{height: 300}}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              width={500}
              height={300}
              data={probabilityData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="model" />
              <YAxis domain={[0, 100]} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey={player1.player} fill="#8884d8" radius={[10, 10, 0, 0]} />
              <Bar dataKey={player2.player} fill="#82ca9d" radius={[10, 10, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Scénarios de sets pour la surface sélectionnée */}
      {hasAdvancedProb && (
        <div style={{marginBottom: 30}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
            <h5 style={{fontSize: 15, marginBottom: 0}}>Scénarios de sets possibles</h5>
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
              <span>Format Grand Chelem</span>
              <label className="switch" style={{
                position: 'relative',
                display: 'inline-block',
                width: '40px',
                height: '24px'
              }}>
                <input 
                  type="checkbox" 
                  checked={isGrandSlam}
                  onChange={() => setIsGrandSlam(!isGrandSlam)}
                  style={{opacity: 0, width: 0, height: 0}}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: isGrandSlam ? '#2196F3' : '#ccc',
                  transition: '.4s',
                  borderRadius: '24px',
                  '&:before': {
                    position: 'absolute',
                    content: '""',
                    height: '16px',
                    width: '16px',
                    left: '4px',
                    bottom: '4px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%',
                    transform: isGrandSlam ? 'translateX(16px)' : 'translateX(0)'
                  }
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '16px',
                    width: '16px',
                    left: '4px',
                    bottom: '4px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%',
                    transform: isGrandSlam ? 'translateX(16px)' : 'translateX(0)'
                  }}></span>
                </span>
              </label>
            </div>
          </div>
          
          <SetScenariosChart 
            player1={player1.player}
            player2={player2.player}
            probability={comparison.advanced.player1} // This should be player1's win probability on this specific surface
            isGrandSlam={isGrandSlam}
          />
        </div>
      )}
      
      {/* Facteurs de service */}
      {factors.serviceStats && factors.serviceStats.player1 && factors.serviceStats.player2 && (
        <div style={{marginBottom: 20}}>
          <h5 style={{fontSize: 15, marginBottom: 5}}>Efficacité du service</h5>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 14}}>
            <thead>
              <tr style={{backgroundColor: '#f0f0f0'}}>
                <th style={{padding: 8, textAlign: "left", border: "1px solid #ddd"}}>Facteur</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player1.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player2.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>Avantage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>% de jeux de service tenus</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.holdPercentage ? Math.round(factors.serviceStats.player1.holdPercentage * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player2.holdPercentage ? Math.round(factors.serviceStats.player2.holdPercentage * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.holdPercentage && factors.serviceStats.player2.holdPercentage && 
                   factors.serviceStats.player1.holdPercentage > factors.serviceStats.player2.holdPercentage ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>1ère balle (in %)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.firstServeIn ? Math.round(factors.serviceStats.player1.firstServeIn * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player2.firstServeIn ? Math.round(factors.serviceStats.player2.firstServeIn * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.firstServeIn && factors.serviceStats.player2.firstServeIn &&
                   factors.serviceStats.player1.firstServeIn > factors.serviceStats.player2.firstServeIn ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Points sur 1ère balle (%)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.firstServeWin ? Math.round(factors.serviceStats.player1.firstServeWin * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player2.firstServeWin ? Math.round(factors.serviceStats.player2.firstServeWin * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.firstServeWin && factors.serviceStats.player2.firstServeWin &&
                   factors.serviceStats.player1.firstServeWin > factors.serviceStats.player2.firstServeWin ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Points sur 2ème balle (%)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.secondServeWin ? Math.round(factors.serviceStats.player1.secondServeWin * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player2.secondServeWin ? Math.round(factors.serviceStats.player2.secondServeWin * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.secondServeWin && factors.serviceStats.player2.secondServeWin &&
                   factors.serviceStats.player1.secondServeWin > factors.serviceStats.player2.secondServeWin ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Aces (%)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.aceRate ? Math.round(factors.serviceStats.player1.aceRate * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player2.aceRate ? Math.round(factors.serviceStats.player2.aceRate * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.serviceStats.player1.aceRate && factors.serviceStats.player2.aceRate &&
                   factors.serviceStats.player1.aceRate > factors.serviceStats.player2.aceRate ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* Facteurs de retour */}
      {factors.returnStats && factors.returnStats.player1 && factors.returnStats.player2 && (
        <div style={{marginBottom: 20}}>
          <h5 style={{fontSize: 15, marginBottom: 5}}>Efficacité du retour</h5>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 14}}>
            <thead>
              <tr style={{backgroundColor: '#f0f0f0'}}>
                <th style={{padding: 8, textAlign: "left", border: "1px solid #ddd"}}>Facteur</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player1.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player2.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>Avantage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>% de breaks</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player1.breakPercentage ? Math.round(factors.returnStats.player1.breakPercentage * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player2.breakPercentage ? Math.round(factors.returnStats.player2.breakPercentage * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player1.breakPercentage && factors.returnStats.player2.breakPercentage &&
                   factors.returnStats.player1.breakPercentage > factors.returnStats.player2.breakPercentage ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Points de retour gagnés (%)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player1.returnPointsWon ? Math.round(factors.returnStats.player1.returnPointsWon * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player2.returnPointsWon ? Math.round(factors.returnStats.player2.returnPointsWon * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.returnStats.player1.returnPointsWon && factors.returnStats.player2.returnPointsWon &&
                   factors.returnStats.player1.returnPointsWon > factors.returnStats.player2.returnPointsWon ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* Forme récente et statistiques globales */}
      {factors.careerForm && factors.recentForm && factors.totalPointsWon && (
        <div>
          <h5 style={{fontSize: 15, marginBottom: 5}}>Forme et statistiques globales</h5>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 14}}>
            <thead>
              <tr style={{backgroundColor: '#f0f0f0'}}>
                <th style={{padding: 8, textAlign: "left", border: "1px solid #ddd"}}>Facteur</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player1.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player2.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>Avantage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>% de victoires (carrière)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.careerForm.player1 ? Math.round(factors.careerForm.player1 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.careerForm.player2 ? Math.round(factors.careerForm.player2 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.careerForm.player1 && factors.careerForm.player2 &&
                   factors.careerForm.player1 > factors.careerForm.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>% de victoires (12 derniers mois)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentForm.player1 ? Math.round(factors.recentForm.player1 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentForm.player2 ? Math.round(factors.recentForm.player2 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentForm.player1 && factors.recentForm.player2 &&
                   factors.recentForm.player1 > factors.recentForm.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>% total de points gagnés</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.totalPointsWon.player1 ? Math.round(factors.totalPointsWon.player1 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.totalPointsWon.player2 ? Math.round(factors.totalPointsWon.player2 * 100) : 0}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.totalPointsWon.player1 && factors.totalPointsWon.player2 &&
                   factors.totalPointsWon.player1 > factors.totalPointsWon.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* Nouvelle section : Forme récente détaillée */}
      {factors.recentFormFromResults && (
        <div style={{marginTop: 20}}>
          <h5 style={{fontSize: 15, marginBottom: 5}}>
            Forme récente détaillée (10 derniers matchs réels)
            <span style={{fontSize: 12, color: '#666', marginLeft: 10}}>
              Qualité des données: {Math.round((factors.recentDataQuality || 0) * 100)}%
            </span>
          </h5>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 14}}>
            <thead>
              <tr style={{backgroundColor: '#f0f0f0'}}>
                <th style={{padding: 8, textAlign: "left", border: "1px solid #ddd"}}>Facteur</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player1.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player2.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>Avantage</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Forme récente (10 derniers matchs)</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  <div>
                    <div style={{fontWeight: 'bold', color: factors.recentFormFromResults.player1.winRate > 0.6 ? 'green' : factors.recentFormFromResults.player1.winRate < 0.4 ? 'red' : 'orange'}}>
                      {factors.recentFormFromResults.player1.formString || 'N/A'}
                    </div>
                    <div style={{fontSize: 12, color: '#666'}}>
                      {Math.round((factors.recentFormFromResults.player1.winRate || 0) * 100)}% 
                      ({factors.recentFormFromResults.player1.matchCount || 0} matchs)
                    </div>
                  </div>
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  <div>
                    <div style={{fontWeight: 'bold', color: factors.recentFormFromResults.player2.winRate > 0.6 ? 'green' : factors.recentFormFromResults.player2.winRate < 0.4 ? 'red' : 'orange'}}>
                      {factors.recentFormFromResults.player2.formString || 'N/A'}
                    </div>
                    <div style={{fontSize: 12, color: '#666'}}>
                      {Math.round((factors.recentFormFromResults.player2.winRate || 0) * 100)}% 
                      ({factors.recentFormFromResults.player2.matchCount || 0} matchs)
                    </div>
                  </div>
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentFormFromResults.player1.winRate && factors.recentFormFromResults.player2.winRate &&
                   factors.recentFormFromResults.player1.winRate > factors.recentFormFromResults.player2.winRate ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              {/* Nouvelle ligne pour la forme ultra-récente */}
              {factors.recentFormFromResults.player1.ultraRecentForm && factors.recentFormFromResults.player2.ultraRecentForm && (
                <tr style={{backgroundColor: '#f8f9fa'}}>
                  <td style={{padding: 8, border: "1px solid #ddd", fontStyle: 'italic'}}>Momentum immédiat (3 derniers matchs)</td>
                  <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                    <div>
                      <div style={{fontWeight: 'bold', fontSize: 14, color: factors.recentFormFromResults.player1.ultraRecentWinRate > 0.6 ? 'green' : factors.recentFormFromResults.player1.ultraRecentWinRate < 0.4 ? 'red' : 'orange'}}>
                        {factors.recentFormFromResults.player1.ultraRecentForm || 'N/A'}
                      </div>
                      <div style={{fontSize: 11, color: '#666'}}>
                        {Math.round((factors.recentFormFromResults.player1.ultraRecentWinRate || 0) * 100)}%
                      </div>
                    </div>
                  </td>
                  <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                    <div>
                      <div style={{fontWeight: 'bold', fontSize: 14, color: factors.recentFormFromResults.player2.ultraRecentWinRate > 0.6 ? 'green' : factors.recentFormFromResults.player2.ultraRecentWinRate < 0.4 ? 'red' : 'orange'}}>
                        {factors.recentFormFromResults.player2.ultraRecentForm || 'N/A'}
                      </div>
                      <div style={{fontSize: 11, color: '#666'}}>
                        {Math.round((factors.recentFormFromResults.player2.ultraRecentWinRate || 0) * 100)}%
                      </div>
                    </div>
                  </td>
                  <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                    {factors.recentFormFromResults.player1.ultraRecentWinRate && factors.recentFormFromResults.player2.ultraRecentWinRate &&
                     factors.recentFormFromResults.player1.ultraRecentWinRate > factors.recentFormFromResults.player2.ultraRecentWinRate ? 
                      <span style={{color: "green"}}>{player1.player}</span> : 
                      <span style={{color: "green"}}>{player2.player}</span>}
                  </td>
                </tr>
              )}
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Matchs sur {surface}</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentFormFromResults.player1.surfaceMatchCount || 0} matchs
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.recentFormFromResults.player2.surfaceMatchCount || 0} matchs
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {(factors.recentFormFromResults.player1.surfaceMatchCount || 0) > (factors.recentFormFromResults.player2.surfaceMatchCount || 0) ? 
                    <span style={{color: "blue"}}>{player1.player}</span> : 
                    <span style={{color: "blue"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Confiance des données</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  <div style={{
                    padding: '2px 6px', 
                    borderRadius: '3px', 
                    backgroundColor: factors.recentFormFromResults.player1.confidence > 0.8 ? '#d4edda' : 
                                   factors.recentFormFromResults.player1.confidence > 0.5 ? '#fff3cd' : '#f8d7da',
                    color: factors.recentFormFromResults.player1.confidence > 0.8 ? '#155724' : 
                           factors.recentFormFromResults.player1.confidence > 0.5 ? '#856404' : '#721c24'
                  }}>
                    {Math.round((factors.recentFormFromResults.player1.confidence || 0) * 100)}%
                  </div>
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  <div style={{
                    padding: '2px 6px', 
                    borderRadius: '3px', 
                    backgroundColor: factors.recentFormFromResults.player2.confidence > 0.8 ? '#d4edda' : 
                                   factors.recentFormFromResults.player2.confidence > 0.5 ? '#fff3cd' : '#f8d7da',
                    color: factors.recentFormFromResults.player2.confidence > 0.8 ? '#155724' : 
                           factors.recentFormFromResults.player2.confidence > 0.5 ? '#856404' : '#721c24'
                  }}>
                    {Math.round((factors.recentFormFromResults.player2.confidence || 0) * 100)}%
                  </div>
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {(factors.recentFormFromResults.player1.confidence || 0) > (factors.recentFormFromResults.player2.confidence || 0) ? 
                    <span style={{color: "blue"}}>{player1.player}</span> : 
                    <span style={{color: "blue"}}>{player2.player}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* Amplification des écarts (v2.2) */}
      {factors.amplification && factors.amplification.amplificationFactor > 1.0 && (
        <div style={{marginTop: 20, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '8px', padding: '15px'}}>
          <h5 style={{fontSize: 15, marginBottom: 10, color: '#856404'}}>
            🚀 Amplification v2.2 appliquée
          </h5>
          <div style={{fontSize: 13, color: '#856404'}}>
            <div><strong>Probabilité brute :</strong> {Math.round(factors.amplification.rawProb1 * 100)}% vs {Math.round((1 - factors.amplification.rawProb1) * 100)}%</div>
            <div><strong>Écart initial :</strong> {Math.round(factors.amplification.rawDifference * 100)}%</div>
            <div><strong>Facteur d'amplification :</strong> x{factors.amplification.amplificationFactor}</div>
            <div><strong>Écart amplifié :</strong> {Math.round(factors.amplification.amplifiedDifference * 100)}%</div>
            <div style={{marginTop: 8, fontStyle: 'italic'}}>
              L'écart a été amplifié pour mieux refléter la différence de niveau réelle entre les joueurs.
            </div>
          </div>
        </div>
      )}

      {/* Probabilités calculées */}
      {hasSimpleElo && hasAdvancedProb && hasMatchStatProb && (
        <div style={{marginTop: 20}}>
          <h5 style={{fontSize: 15, marginBottom: 5}}>Probabilités calculées</h5>
          <table style={{width: "100%", borderCollapse: "collapse", fontSize: 14}}>
            <thead>
              <tr style={{backgroundColor: '#f0f0f0'}}>
                <th style={{padding: 8, textAlign: "left", border: "1px solid #ddd"}}>Modèle</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player1.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>{player2.player}</th>
                <th style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>Favori</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Elo</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.simpleElo.player1}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.simpleElo.player2}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.simpleElo.player1 > comparison.simpleElo.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Service/Retour</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {Math.round(factors.matchStatProb.player1 * 100)}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {Math.round(factors.matchStatProb.player2 * 100)}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {factors.matchStatProb.player1 > factors.matchStatProb.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
              <tr style={{fontWeight: 'bold', backgroundColor: '#e8f4f8'}}>
                <td style={{padding: 8, border: "1px solid #ddd"}}>Modèle avancé final</td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.advanced.player1}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.advanced.player2}%
                </td>
                <td style={{padding: 8, textAlign: "center", border: "1px solid #ddd"}}>
                  {comparison.advanced.player1 > comparison.advanced.player2 ? 
                    <span style={{color: "green"}}>{player1.player}</span> : 
                    <span style={{color: "green"}}>{player2.player}</span>}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SurfaceStatsDetail; 